import { ChevronDownIcon } from '@fluentui/react-northstar';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PaneHandle } from '../../shared/components/PaneHandle/PaneHandle';
import { TicketDetailBar } from '../../shared/components/TicketDetailInfo/TicketDetailBar';
import { TicketDetailCompact } from '../../shared/components/TicketDetailInfo/TicketDetailCompact';
import { TicketDetailInfo } from '../../shared/components/TicketDetailInfo/TicketDetailInfo';
import { TicketDetailPopup } from '../../shared/components/TicketDetailPopup/TicketDetailPopup';
import { EDIT_TICKET_STORAGE_KEYS, getPaneFlag, setPaneFlag } from './storageKeys';
import AppCSS from './TicketDetailCard.module.css';

interface Props {
  ticketData: any;
  closePopup?: (value: boolean) => void;
  showViewMore?: boolean;
  idColorPurple?: boolean;
  attachmentsCount?: number;
  commentsCount?: number;
  isBoardMobileView?: boolean;
  detailCardApprovalsList?: TicketApprovals[];
  detailCardTaskList?: TicketTask[];
  isTimeTrackingEnabled?: boolean;
  /**
   * Set once a total has been reported for this ticket; overrides the loaded ticket's stale
   * total. `modifiedDate` is absent unless the change was made on this page.
   */
  agentTimeUpdate?: { totalMinutes: number; modifiedDate?: string };
  /**
   * On the edit-ticket page: on desktop the card folds to a one-row bar, on a phone it starts
   * compact. Each is remembered across tickets.
   */
  collapsible?: boolean;
}

const TicketDetailCardImpl = ({
  ticketData,
  showViewMore,
  idColorPurple,
  attachmentsCount,
  commentsCount,
  isBoardMobileView,
  detailCardApprovalsList,
  detailCardTaskList,
  isTimeTrackingEnabled,
  agentTimeUpdate,
  collapsible = false
}: Props) => {
  const { t } = useTranslation();
  const [isPopupShow, setIsPopupShow] = useState(false);
  const [collapsed, setCollapsedState] = useState(() => collapsible && getPaneFlag(EDIT_TICKET_STORAGE_KEYS.headerCollapsed));

  const toggleCollapsed = useCallback(() => {
    setCollapsedState(prev => {
      setPaneFlag(EDIT_TICKET_STORAGE_KEYS.headerCollapsed, !prev);
      return !prev;
    });
  }, []);

  // The phone's card starts compact - the activity is what the page is for there - and
  // remembers being opened. The whole card is the tap target; a small handle is no thumb's size.
  const [mobileExpanded, setMobileExpanded] = useState(() => getPaneFlag(EDIT_TICKET_STORAGE_KEYS.mobileCardExpanded));
  const mobileCompact = collapsible && !mobileExpanded;
  const toggleMobile = useCallback(() => {
    setMobileExpanded(prev => {
      setPaneFlag(EDIT_TICKET_STORAGE_KEYS.mobileCardExpanded, !prev);
      return !prev;
    });
  }, []);
  const onMobileKey = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleMobile();
    }
  }, [toggleMobile]);

  const hideOrShowPopup = (value: boolean) => {
    setIsPopupShow(value);
  };

  return (
    <>
      <TicketDetailPopup
        attachmentsCount={attachmentsCount}
        commentsCount={commentsCount}
        isBoardMobileView={isBoardMobileView}
        ticketData={ticketData}
        open={isPopupShow}
        closePopup={hideOrShowPopup}
        detailCardApprovalsList={detailCardApprovalsList}
        detailCardTaskList={detailCardTaskList}
      />
      <div className={`hidden md:block relative pt-0.5 pb-1 px-0.5 ticket-card-detail print-hidden`}>
        {/* For desktop. Both forms stay mounted and the one not in use is folded away: swapped
            in and out, each mount recreated the requester's avatar, which shows initials until
            its photo loads, so the picture flickered on every collapse and expand. Folded to
            zero height rather than display: none, so each keeps its real width - the bar's fit
            and the lifecycle strip measure correctly while hidden. */}
        {collapsible && (
          <div className={collapsed ? undefined : AppCSS['folded-away']}>
            <TicketDetailBar
              ticketData={ticketData}
              isTimeTrackingEnabled={isTimeTrackingEnabled}
              agentTimeUpdate={agentTimeUpdate}
            />
          </div>
        )}
        <div className={collapsed ? AppCSS['folded-away'] : undefined}>
          <TicketDetailInfo
            ticketData={ticketData}
            attachmentsCount={attachmentsCount}
            commentsCount={commentsCount}
            isBoardMobileView={isBoardMobileView}
            detailCardApprovalsList={detailCardApprovalsList}
            detailCardTaskList={detailCardTaskList}
            isTimeTrackingEnabled={isTimeTrackingEnabled}
            agentTimeUpdate={agentTimeUpdate}
          />
        </div>
        {collapsible && (
          <PaneHandle
            direction={collapsed ? 'down' : 'up'}
            label={t(collapsed ? 'ticket-details.panes.expand-header' : 'ticket-details.panes.collapse-header')}
            onClick={toggleCollapsed}
            className={AppCSS['header-handle']}
          />
        )}
      </div>
      <div
        className={`block md:hidden pt-0.5 pb-1 px-0.5 overflow-auto ${AppCSS['ticket-detail-card-mobile']} ${showViewMore && !mobileCompact && AppCSS['ticket-detail-card-res']} ${collapsible ? AppCSS['mobile-toggle'] : ''} print-hidden`}
        {...(collapsible ? {
          role: 'button',
          tabIndex: 0,
          'aria-expanded': !mobileCompact,
          // No aria-label: it would replace the card's text as the button's name, and a
          // screen reader would announce "Expand ticket details" and none of the ticket.
          onClick: toggleMobile,
          onKeyDown: onMobileKey
        } : {})}
      >
        {/* For Mobile */}
        <div className={AppCSS['mobile-card']}>
          {/* Both forms stay mounted, the one not in use folded away, for the same reason as
              on desktop: swapping them recreated the requester's avatar and its photo flickered. */}
          {collapsible && (
            <div className={`${AppCSS['mobile-compact']} ${mobileCompact ? '' : AppCSS['folded-away']}`}>
              <TicketDetailCompact ticketData={ticketData} />
            </div>
          )}
          <div className={mobileCompact ? AppCSS['folded-away'] : undefined}>
            <TicketDetailInfo
              attachmentsCount={attachmentsCount}
              commentsCount={commentsCount}
              isBoardMobileView={isBoardMobileView}
              idColorPurple={idColorPurple}
              ticketData={ticketData}
              showViewMore={showViewMore}
              showDetailPopup={hideOrShowPopup}
              style={{ padding: '0.7rem 1.5rem' }}
            />
          </div>
          {collapsible && (
            <ChevronDownIcon
              size="small"
              aria-hidden="true"
              className={`${AppCSS['mobile-chevron']} ${mobileCompact ? '' : AppCSS['mobile-chevron-open']}`}
            />
          )}
        </div>
      </div>
    </>
  );
};

export const TicketDetailCard = React.memo(TicketDetailCardImpl);
